import { html } from '../application_element/element.js';
import ChartElement from '../chart_element/element.js';
import { axisMax, share, ticks } from '../../lib/chart_scale.js';
import style from './style.lit.css';

/** Beyond this, time labels collide; one in every `n` is drawn instead. */
const MAX_TIME_LABELS = 7;

/**
 * Base for the charts drawn against a value axis. Not registered as an element —
 * it owns the plot shell (axis, gridlines, bucket labels) so a bar and a line
 * read against exactly the same scale.
 *
 * Subclasses render the data into `renderPlot`, in an element classed `body`.
 *
 * @prop height - Plot height in pixels ( number, default: 200 )
 *
 * @cssprop [--grid-color: var(--gray-200, #e9ecef)] - Gridline colour.
 */
export default class PlotElement extends ChartElement {
  static properties = {
    height: { type: Number },
  };

  static styles = [ChartElement.styles, style];

  constructor() {
    super();
    this.height = 200;
  }

  /** Bars and lines stop on a gridline, so they measure against this, not the peak. */
  get axisTop() {
    return axisMax(this.peak);
  }

  get scale() {
    return ticks(this.peak);
  }

  get stride() {
    if (!this.timeseries) return 1;

    return Math.ceil(this.rows.length / MAX_TIME_LABELS);
  }

  /** Where a value sits in the plot, as a percentage of its height. */
  heightOf(row) {
    return share(this.numberOf(row), this.axisTop);
  }

  markFor(tick) {
    return this.formatted(tick);
  }

  /** Counted from the end, so the most recent bucket is always named. */
  showsLabel(index) {
    return (this.rows.length - 1 - index) % this.stride === 0;
  }

  // A thinned label may spill into the blank columns beside it, which is the
  // only way a date fits. The two on the ends align inward, so nothing
  // overhangs the plot.
  labelClass(index) {
    if (this.stride === 1) return 'label';
    if (index === 0) return 'label spilling start';
    if (index === this.rows.length - 1) return 'label spilling end';

    return 'label spilling';
  }

  renderAxis() {
    return html`
      <div class="axis" aria-hidden="true">
        <!-- Every mark is positioned, so the column has nothing to size to.
             This one is in flow but invisible, and the top tick is the widest. -->
        <span class="gauge">${this.markFor(this.axisTop)}</span>
        ${this.scale.map(
          tick => html`
            <span class="mark" style="bottom: ${share(tick, this.axisTop)}%">
              ${this.markFor(tick)}
            </span>
          `
        )}
      </div>
      <div class="grid" aria-hidden="true">
        ${this.scale.map(
          tick =>
            html`<span
              class="line"
              style="bottom: ${share(tick, this.axisTop)}%"></span>`
        )}
      </div>
    `;
  }

  renderLabels() {
    return html`
      <div class="labels">
        ${this.rows.map(
          (row, index) => html`
            <span class=${this.labelClass(index)} title=${this.labelOf(row)}>
              <span class="text"
                >${this.showsLabel(index) ? this.tickLabelOf(row) : ''}</span
              >
            </span>
          `
        )}
      </div>
    `;
  }

  /**
   * @param {unknown} body the drawn data, classed `body` so the grid can place it
   */
  renderPlot(body) {
    return html`
      <figure>
        ${this.renderCaption()}
        <div
          class="plot"
          style="--chart-height: ${this.height}px"
          role="img"
          aria-label=${this.caption || this.t('chart')}>
          ${this.renderAxis()} ${body} ${this.renderLabels()}
        </div>
        <ul class="sr-only">
          ${this.descriptions.map(entry => html`<li>${entry}</li>`)}
        </ul>
      </figure>
    `;
  }
}

PlotElement.extendTranslationsAs('xs-plot-element');
